import { useMemo, useState } from "react";
import MainLayout from "../../layout/MainLayout";
import { Button1, Button2, QuickActionSelect, Select } from "../index";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import { DatePicker, Input, message } from "antd";
import dateIcon from "../../assets/icons/calendar-icon.png";
import { useNavigate } from "react-router-dom";
import { socialPaymentsApi } from "../../api/endpoints";
import { useAllGroupExpenses, useAccounts } from "../../data/live";

const displayName = (user, fallback = "Not available") =>
  user?.displayName ||
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
  user?.email ||
  fallback;

const money = (amount, currency) =>
  `${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency || ""}`.trim();

const SettleUp = () => {
  const navigate = useNavigate();
  const { items: expenses, loading, refresh } = useAllGroupExpenses();
  const { accounts } = useAccounts();
  const [splitId, setSplitId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const unpaidSplits = useMemo(
    () =>
      expenses.flatMap((expense) =>
        (expense.splits || [])
          .filter((split) => !split.isPaid)
          .map((split) => ({
            ...split,
            expense,
          }))
      ),
    [expenses]
  );

  const selectedSplit =
    unpaidSplits.find((split) => split.id === splitId) || unpaidSplits[0];
  const selectedAccount = accounts.find(
    (account) => account.currency === selectedSplit?.expense?.currency
  );

  const handleSettle = async () => {
    if (!selectedSplit) return;
    setSubmitting(true);
    try {
      await socialPaymentsApi.markSplitPaid(selectedSplit.id);
      await refresh();
      message.success("Split marked as paid");
    } catch (error) {
      message.error(error.response?.data?.error || "Unable to settle split");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MainLayout
        expand={false}
        NavName={"Split Payment"}
        navbarChildren={<QuickActionSelect className="rounded-5" />}
      >
        <div>
          <div className="container mt-5">
            <div className="row">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-5 fw-semibold">Settle Up</span>
                <Button2
                  buttonName={"New Group"}
                  divClassName={"gap-2"}
                  className={"btn-dark"}
                  icon={<img src={plusIcon} alt="add" />}
                  onClick={() => navigate("/create-group")}
                />
              </div>

              <div className="col-12 px-4 mt-3">
                <div className="row border rounded-3 p-3">
                  {loading ? (
                    <div className="p-5 text-center text-muted">Loading open splits...</div>
                  ) : !selectedSplit ? (
                    <div className="p-5 text-center text-muted">No unpaid splits found.</div>
                  ) : (
                    <>
                      <div className="col-lg-6">
                        <div className="d-flex flex-column gap-3 pe-3">
                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Open split</label>
                            <Select
                              className="p-3"
                              value={selectedSplit.id}
                              options={unpaidSplits.map((split) => ({
                                value: split.id,
                                name: `${split.expense.description} - ${displayName(split.user, split.userId)}`,
                              }))}
                              onChange={(e) => setSplitId(e.target.value)}
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Payer</label>
                            <Input
                              className="p-3"
                              value={displayName(selectedSplit.user, selectedSplit.userId)}
                              readOnly
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Pay to</label>
                            <Input
                              className="p-3"
                              value={displayName(
                                selectedSplit.expense.payer,
                                selectedSplit.expense.paidBy
                              )}
                              readOnly
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Amount</label>
                            <Input
                              className="p-3 w-100"
                              value={money(
                                selectedSplit.amountOwed,
                                selectedSplit.expense.currency
                              )}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="d-flex flex-column gap-3 ps-3">
                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Account</label>
                            <Input
                              className="p-3"
                              value={
                                selectedAccount
                                  ? `${selectedAccount.currency} ${selectedAccount.accountNumber || selectedAccount.id}`
                                  : "No matching wallet"
                              }
                              readOnly
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Transaction Line</label>
                            <Input
                              className="p-3"
                              value={selectedSplit.expense.description}
                              readOnly
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">Date</label>
                            <DatePicker
                              className="p-3"
                              placeholder="Select date"
                              suffixIcon={<img src={dateIcon} alt="date" />}
                            />
                          </div>

                          <div className="d-flex flex-column gap-2">
                            <label className="fs12px">From group</label>
                            <Input
                              className="p-3"
                              value={selectedSplit.expense.group?.name || "Not set"}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end align-items-center gap-3 mt-5">
                        <Button1
                          buttonName={"Save as Draft"}
                          className={"btn-outline-success"}
                          disabled
                        />
                        <Button1
                          buttonName={submitting ? "Settling..." : "Settle"}
                          className={"btn-dark"}
                          onClick={handleSettle}
                          disabled={submitting}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default SettleUp;
